import * as TFF from '@tff/types/TFF';
import { MetaValue } from '@tff/types/TFF/tfa-amend-ancillary-service';
import { SeatDetails } from '@tff/types/TFF/tfa-amend-ancillary-service';
import { SeatsByPax, SsrsByPax, SurchargeType } from '../../../../types/ancillaryTypes';

/** Returns ssr "code" value from Metavalues array */
const findAirlineCode = (code: string, rows: MetaValue[]): string => {
  const foundItem = rows.find(i => i.code === code);
  return foundItem ? foundItem.airlineSsrCode : code;
};

/** Generates removeSsrIds object for ssr-update request payload */
const generateSsrPaxToRemove = (ancillaries: TFF.Ancillaries, paxId: string): string => {
  const foundKey = Object.keys(ancillaries).find((ancillaryKey): boolean => {
    const processedAncillaryKey: string = ancillaryKey.replace(/~\d+$/, '').replaceAll('~', '').replaceAll('-', '');
    return paxId === processedAncillaryKey;
  });
  return foundKey as string;
};

/** Gets SSR details for removed SSRs */
const getRemovedSsrDetails = (
  ancillaries: TFF.Ancillaries,
  ssrId: string,
  surchargeValues?: SurchargeType,
): TFF.SsrDetails | undefined => {
  const ancillary = ancillaries[ssrId];
  if (!ancillary) return undefined;

  const code = ancillary.TfaDetails?.Code ?? ancillary.Type;
  const segmentOnd = ancillary.SegmentOnd ?? '';
  const totalAmount = ancillary.TotalAmount ?? 0;

  const parseNumericValue = (value: string | number): number => {
    return typeof value === 'string' ? parseFloat(value.replace(',', '.')) : Number(value);
  };

  const findSurchargeKey = (): string | undefined => {
    const regex = new RegExp(`^${segmentOnd}-${code}-remove-\\d+-\\d+$`);
    return Object.keys(surchargeValues || {}).find(key => regex.test(key));
  };

  const surchargeKey = findSurchargeKey();
  const priceToCharge = surchargeKey
    ? parseNumericValue(surchargeValues[surchargeKey])
    : -totalAmount;

  return {
    code,
    airlineSsrCode: ancillary.Type,
    segmentOnd,
    amount: 1,
    price: -totalAmount,
    priceToCharge,
  };
};

/** Returns ssr price to send back-end for Rebook flow */
export const formatSsrPrice = (
  newPrice: number,
  originalPrice: number,
  adjustedSurcharge: number,
  originalAmount: number,
  newAmount: number,
): number => {
  if (originalAmount === 0) {
    return adjustedSurcharge > 0 ? newPrice / newAmount : 0;
  } else if (originalPrice === newPrice) {
    return newPrice / newAmount || originalPrice / newAmount;
  } else if (originalPrice < newPrice) {
    return adjustedSurcharge === newPrice - originalPrice
      ? newPrice / newAmount
      : adjustedSurcharge === 0
      ? originalPrice / newAmount
      : (originalPrice / originalAmount + adjustedSurcharge) / newAmount;
  } else {
    return originalPrice / newAmount;
  }
};

/**
 * Generates paxData array for ssr-update request payload
 * Forms the array and its objects in the requested structure
 * 
 * When surchargeValues are provided and not empty, includes removeSsrDetails in payload (confirm action).
 * When undefined or empty, omits removeSsrDetails (validate action)
 */
export const generatePaxDataForAmendRequest = (
  rebooking: boolean | undefined,
  newSsrsToAdd: SsrsByPax,
  outboundRaw: MetaValue[],
  inboundRaw: MetaValue[],
  bookedAncillaries: TFF.Ancillaries,
  surchargeValues?: SurchargeType,
  seatAncillary?: SeatsByPax,
  newOnds?: {
    Outbound: string;
    Inbound: string;
  },
) => {
  const paxData: TFF.PaxData[] = [];
  Object.keys(newSsrsToAdd).forEach(paxId => {
    let addedSsrs: TFF.SsrDetails[] = [];
    let addedSeats: SeatDetails[];
    let removedSsrs: string[] = [];
    let removeSsrDetails: TFF.SsrDetails[] = [];

    const containsSubchargeValueEntries = surchargeValues && Object.keys(surchargeValues).length > 0;

    if (newSsrsToAdd[paxId].length > 0) {
      newSsrsToAdd[paxId].forEach(ssr => {
        if (ssr.code.includes('BA0')) {
          return;
        } else {
          if (ssr.code.includes('remove')) {
            const processPaxId = `${ssr.segmentOnd}${paxId}${findAirlineCode(
              ssr.code.replace('-remove', ''),
              outboundRaw ?? inboundRaw,
            )}`.replaceAll('-', '');
            const ssrId = generateSsrPaxToRemove(bookedAncillaries, processPaxId);
            removedSsrs.push(ssrId);
            if (containsSubchargeValueEntries) {
              const ssrDetails = getRemovedSsrDetails(bookedAncillaries, ssrId, surchargeValues);
              if (ssrDetails) {
                removeSsrDetails.push(ssrDetails);
              }
            }
          } else {
            if (surchargeValues) {
              setSsrPriceToCharge(rebooking, ssr, newOnds, outboundRaw, inboundRaw, surchargeValues, bookedAncillaries);
            }
            addedSsrs.push(ssr);
          }
        }
      });
    }

    addedSeats = getAddedSeats(paxId, surchargeValues, seatAncillary);

    const paxDataItem: TFF.PaxData = {
      paxId,
      removeSsrIds: removedSsrs,
      addSsrs: addedSsrs,
      addSeats: addedSeats,
    };

    if (containsSubchargeValueEntries) {
      paxDataItem.removeSsrDetails = removeSsrDetails;
    }

    paxData.push(paxDataItem);
  });

  return paxData;
};

/**
 * Sets the priceToCharge for an SSR based on surcharge values
 * Handles both rebooking and regular amend flows with different pricing logic
 */
export const setSsrPriceToCharge = (
  rebooking: boolean | undefined,
  ssr: TFF.SsrDetails,
  newOnds:
    | {
        Outbound: string;
        Inbound: string;
      }
    | undefined,
  outboundRaw: MetaValue[],
  inboundRaw: MetaValue[],
  surchargeValues: SurchargeType,
  bookedAncillaries: TFF.Ancillaries,
) => {
  const regexForSurchargeKeys = new RegExp(`^${ssr.segmentOnd}-${ssr.code}-\\d+-\\d+$`);

  const keyWithNumber = Object.keys(surchargeValues).find(key => regexForSurchargeKeys.test(key));

  if (keyWithNumber) {
    const originalPrice =
      Object.values(bookedAncillaries).filter(
        i => (i.SegmentOnd == ssr.segmentOnd && i.Type == ssr.airlineSsrCode) || i.TfaDetails?.Code == ssr.code,
      )[0]?.TotalAmount ?? 0;
    const [originalSsrAmount, newSsrAmount] = [...keyWithNumber.split('-')].reverse();

    const formattedSurcharge = Number(parseInt(surchargeValues[keyWithNumber]).toFixed(2));

    if (rebooking) {
      // Check the price of the ssr
      let newAncillaryPrice = ssr.price;

      if (!newAncillaryPrice && newOnds) {
        const isOutbound = ssr.segmentOnd.replaceAll('-', '') === newOnds.Outbound;
        const arrayToSearch = isOutbound ? outboundRaw : inboundRaw;

        newAncillaryPrice = arrayToSearch
          .filter(metaValue => metaValue.code === ssr.code)
          .map(metaValue => metaValue.price.amount)[0];
      }

      ssr.priceToCharge = Number(
        formatSsrPrice(
          (newAncillaryPrice ?? 0) * parseInt(newSsrAmount),
          originalPrice * parseInt(originalSsrAmount),
          formattedSurcharge,
          parseInt(originalSsrAmount),
          parseInt(newSsrAmount),
        ).toFixed(2),
      );
    } else {
      ssr.priceToCharge = Number(formattedSurcharge);
    }
  }
};

/**
 * Collects and processes seat details for a passenger to be added in the request payload
 * Supports multiple seats per passenger across different segments using composite keys (paxId-segmentOnd)
 *
 * @param paxId - Passenger ID to collect seats for
 * @param surchargeValues - Optional surcharge adjustments from Cost Overview table
 * @param seatAncillary - Seat ancillaries by passenger, keyed by paxId or composite paxId-segmentOnd
 * @returns Array of seat details with priceToCharge set based on surcharge or original price
 *
 * Pricing logic:
 * - If the surcharge key exists with non-zero value: uses adjusted surcharge (manual changes)
 * - If the surcharge key exists with zero value: uses original seat price (retained seats)
 * - If no surcharge key exists: uses original seat price (default)
 */
const getAddedSeats = (paxId: string, surchargeValues?: SurchargeType, seatAncillary?: SeatsByPax) => {
  const seats = seatAncillary ?? {};
  let addedSeats: SeatDetails[] = [];

  // Collect all seats for this paxId (handles both simple paxId keys and composite paxId-segmentOnd keys)
  Object.keys(seats).forEach(key => {
    if (key === paxId || key.startsWith(`${paxId}-`)) {
      const seat = seats[key];
      
      const regexForSurchargeKeys = new RegExp(`^${seat.segmentOnd}-${seat.code}-\\d+-\\d+$`);
      const keyWithNumber = surchargeValues ? Object.keys(surchargeValues).find(k => regexForSurchargeKeys.test(k)) : undefined;

      if (keyWithNumber && surchargeValues) {
        const surchargeValue = Number(surchargeValues[keyWithNumber].replace(',', '.'));
        // If the surcharge is 0, use the original price (retained seats). Otherwise, use surcharge (manual changes)
        seat.priceToCharge = surchargeValue === 0 ? (seat.price ?? 0) : surchargeValue;
      } else {
        seat.priceToCharge = seat.price ?? 0;
      }

      addedSeats.push(seat);
    }
  });

  return addedSeats;
};
